import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Download, LayoutGrid, List, MessageCircle, Repeat, Search } from "lucide-react";
import { showAnswer } from "@/components/QuoteForm";
import { toast } from "sonner";
import { api, errMsg, STAGES, stageOf, timeAgo } from "@/lib/api";
import { PageHead } from "@/pages/vendor/Shell";
import { LeadDrawer } from "@/pages/vendor/LeadDrawer";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BouncedFlag } from "@/components/DeliveryBadge";

function LeadCard({ l, onOpen }: { l: any; onOpen: () => void }) {
  return (
    <button draggable onDragStart={(e) => e.dataTransfer.setData("lead", l.id)} onClick={onOpen} data-testid={`lead-card-${l.id}`}
      className="hard-shadow-hover w-full cursor-grab rounded-xl border border-border bg-white p-3 text-left hover:border-emerald-500/60 active:cursor-grabbing">
      <div className="flex items-start justify-between gap-2">
        <p className="truncate text-sm font-semibold">{l.name}</p>
        {l.unread && <MessageCircle className="h-3.5 w-3.5 shrink-0 text-emerald-600" />}
      </div>
      <p className="truncate text-xs text-muted-foreground">{l.email}</p>
      {l.email_bounced && <BouncedFlag l={l} />}
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">{l.event_name}</span>
        {l.quote_submitted && <span className="rounded bg-cyan-50 px-1.5 py-0.5 text-[10px] font-semibold text-cyan-700">Quote</span>}
        {l.preregistered && <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${l.checked_in ? "bg-green-50 text-green-700" : "bg-violet-50 text-violet-700"}`} data-testid={`lead-prereg-badge-${l.id}`}>{l.checked_in ? "Checked in" : "Pre-reg"}</span>}
        {l.interaction_count > 1 && <span className="flex items-center gap-0.5 text-[10px] text-amber-700"><Repeat className="h-3 w-3" />{l.interaction_count}</span>}
        <span className="ml-auto text-[10px] text-muted-foreground">{timeAgo(l.created_at)}</span>
      </div>
    </button>
  );
}

export default function Leads() {
  const [params, setParams] = useSearchParams();
  const [leads, setLeads] = useState<any[]>([]);
  const [events, setEvents] = useState<any[]>([]);
  const [view, setView] = useState<"board" | "list">("board");
  const [q, setQ] = useState("");
  const [eventId, setEventId] = useState("all");
  const [types, setTypes] = useState<any[]>([]);
  const [typeId, setTypeId] = useState("all");
  const curType = types.find((t) => t.id === typeId);
  const openId = params.get("lead");

  const load = () => api.get("/vendor/leads", { params: { q: q || undefined, event_id: eventId === "all" ? undefined : eventId, event_type_id: typeId === "all" ? undefined : typeId } }).then((r) => setLeads(r.data));
  useEffect(() => {
    api.get("/vendor/events").then((r) => setEvents(r.data));
    api.get("/event-types").then((r) => setTypes(r.data));
  }, []);
  useEffect(() => {
    const t = setTimeout(load, 250);
    const poll = setInterval(load, 15000);
    return () => { clearTimeout(t); clearInterval(poll); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, eventId, typeId]);

  const move = async (id: string, stage: string) => {
    setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, stage } : l)));
    try {
      await api.patch(`/vendor/leads/${id}`, { stage });
    } catch (e) {
      toast.error(errMsg(e));
      load();
    }
  };

  const exportCsv = async () => {
    try {
      const { data } = await api.get("/vendor/leads/export.csv", { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(data);
      a.download = "leads.csv";
      a.click();
    } catch (e: any) {
      toast.error(e?.response?.status === 402 ? "CSV export is included in the Pro plan" : errMsg(e));
    }
  };

  const open = (id: string | null) => setParams(id ? { lead: id } : {});

  return (
    <div>
      <PageHead eyebrow="CRM pipeline" title="Leads">
        <Button variant="outline" onClick={exportCsv} data-testid="leads-export-button"><Download className="mr-1 h-4 w-4" /> Export CSV</Button>
      </PageHead>
      <div className="mb-5 flex flex-wrap gap-3">
        <div className="relative min-w-[220px] flex-1">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email" className="pl-9" data-testid="leads-search-input" />
        </div>
        <Select value={eventId} onValueChange={setEventId}>
          <SelectTrigger className="w-[220px]" data-testid="leads-event-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All events</SelectItem>
            {events.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={typeId} onValueChange={(v) => { setTypeId(v); if (v !== "all") setView("list"); }}>
          <SelectTrigger className="w-[200px]" data-testid="leads-type-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All event types</SelectItem>
            {types.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex rounded-lg border border-border p-0.5">
          <Button size="sm" variant={view === "board" ? "secondary" : "ghost"} onClick={() => setView("board")} data-testid="leads-view-board"><LayoutGrid className="h-4 w-4" /></Button>
          <Button size="sm" variant={view === "list" ? "secondary" : "ghost"} onClick={() => setView("list")} data-testid="leads-view-list"><List className="h-4 w-4" /></Button>
        </div>
      </div>

      {view === "board" ? (
        <div className="flex gap-3 overflow-x-auto pb-4" data-testid="leads-board">
          {STAGES.map((s) => {
            const col = leads.filter((l) => l.stage === s.key);
            return (
              <div key={s.key} onDragOver={(e) => e.preventDefault()} onDrop={(e) => move(e.dataTransfer.getData("lead"), s.key)}
                className="w-72 shrink-0 rounded-2xl border border-border bg-muted/60 p-3" data-testid={`leads-column-${s.key}`}>
                <div className="mb-3 flex items-center gap-2 px-1">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ background: s.color }} />
                  <span className="text-sm font-semibold">{s.label}</span>
                  <span className="ml-auto font-mono text-xs text-muted-foreground">{col.length}</span>
                </div>
                <div className="space-y-2">
                  {col.map((l) => <LeadCard key={l.id} l={l} onOpen={() => open(l.id)} />)}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-border" data-testid="leads-table">
          <table className="w-full text-sm">
            <thead className="bg-card text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr><th className="p-3">Name</th><th className="p-3">Event</th><th className="p-3">Stage</th>
                {curType ? curType.questions.map((qq: any) => <th key={qq.id} className="whitespace-nowrap p-3" data-testid={`leads-compare-col-${qq.id}`}>{qq.label}</th>) : <><th className="p-3">Tags</th><th className="p-3">Marketing</th></>}
                <th className="p-3">Created</th></tr>
            </thead>
            <tbody className="divide-y divide-border">
              {leads.map((l) => (
                <tr key={l.id} onClick={() => open(l.id)} className="cursor-pointer hover:bg-muted" data-testid={`leads-row-${l.id}`}>
                  <td className="p-3"><p className="font-semibold">{l.name}</p><p className="text-xs text-muted-foreground">{l.email}</p>{l.email_bounced && <BouncedFlag l={l} />}</td>
                  <td className="p-3 text-muted-foreground">{l.event_name}</td>
                  <td className="p-3"><span className="rounded-full px-2 py-0.5 text-xs font-semibold" style={{ background: `${stageOf(l.stage).color}22`, color: stageOf(l.stage).color }}>{stageOf(l.stage).label}</span></td>
                  {curType ? curType.questions.map((qq: any) => <td key={qq.id} className="p-3 text-xs">{showAnswer(l.base_answers?.[qq.id])}</td>) : <>
                    <td className="p-3 text-xs text-muted-foreground">{l.tags.join(", ")}</td>
                    <td className="p-3 text-xs">{l.marketing_consent ? "Opted in" : "—"}</td>
                  </>}
                  <td className="p-3 text-xs text-muted-foreground">{timeAgo(l.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {leads.length === 0 && <p className="p-8 text-center text-sm text-muted-foreground">No leads match.</p>}
        </div>
      )}
      {openId && <LeadDrawer leadId={openId} onClose={() => open(null)} onChanged={load} />}
    </div>
  );
}
